import type { ChatSessionStatus } from "@qw/graphql";

import { getChatSessionBusyLabel, isAssistantMessageContentPart, isChatSessionBusy } from "./chat.helpers";
import type { ExtendedUIMessage } from "./chat.types";
import type { AiSdkChatStatus } from "./hooks/action-for-stream-state-event";
import { ElicitationSubmitPhase } from "./hooks/elicitation-submit-phase";
import { getRenderKey } from "./render-identity";

export interface AssistantProgressSignals {
  status: AiSdkChatStatus;
  elicitationSubmitPhase: ElicitationSubmitPhase;
  sessionStatus: ChatSessionStatus | null | undefined;
}

export type AssistantProgressPresentation =
  | { kind: "none" }
  | { kind: "standalone"; label: string }
  | {
      kind: "message-footer";
      messageKey: string;
      progress: AssistantMessageProgress;
    };

export interface AssistantMessageProgress {
  isStreaming: boolean;
  label: string;
}

export type AssistantFooterProgress = Extract<AssistantProgressPresentation, { kind: "message-footer" }>;

const NO_ASSISTANT_PROGRESS = { kind: "none" } as const;

/**
 * Resolves the shared progress copy from the client, submit, and server-session signals.
 * More-specific observable work wins over older state that may still be clearing.
 */
export const resolveAssistantProgressLabel = ({
  status,
  elicitationSubmitPhase,
  sessionStatus,
}: AssistantProgressSignals): string | null => {
  if (status === "streaming") {
    return "Alby is responding...";
  }
  if (elicitationSubmitPhase === ElicitationSubmitPhase.Posting) {
    return "Submitting response...";
  }
  if (isChatSessionBusy(sessionStatus)) {
    return getChatSessionBusyLabel(sessionStatus);
  }
  if (elicitationSubmitPhase === ElicitationSubmitPhase.WaitingForStream || status === "submitted") {
    return "Alby is thinking...";
  }
  return null;
};

/**
 * Gives chat progress a single presentation owner. Active work is attached to the last
 * assistant message when possible. While the SDK is awaiting a response with no visible
 * assistant content, or when there is no assistant message to own a footer, a standalone
 * progress row keeps the signal visible instead.
 */
export const resolveAssistantProgressPresentation = ({
  messages,
  ...signals
}: AssistantProgressSignals & { messages: readonly ExtendedUIMessage[] }): AssistantProgressPresentation => {
  const label = resolveAssistantProgressLabel(signals);
  if (!label) {
    return NO_ASSISTANT_PROGRESS;
  }

  const lastMessage = messages[messages.length - 1];
  const lastMessageIsVisibleAssistant =
    lastMessage?.role === "assistant" && Boolean(lastMessage.parts?.some(isAssistantMessageContentPart));
  const isAwaitingSdkResponse = signals.status === "submitted" || signals.status === "streaming";

  if ((isAwaitingSdkResponse && !lastMessageIsVisibleAssistant) || lastMessage?.role !== "assistant") {
    return { kind: "standalone", label };
  }

  return {
    kind: "message-footer",
    messageKey: getRenderKey(lastMessage),
    progress: {
      isStreaming: signals.status === "streaming",
      label,
    },
  };
};
